import { Client, Conversation } from '@twilio/conversations';
import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { update } from 'services/utilsService';
import {
  addMessage,
  getConversations,
  getMessages,
  getUnreadMessagesCountAction,
  setAllMessagesReadAction,
} from 'state/actions/chatActions';
import {
  ADD_MESSAGE,
  APPEND_PREVIOUS_MESSAGE_FAILURE,
  APPEND_PREVIOUS_MESSAGE_REQUEST,
  APPEND_PREVIOUS_MESSAGE_SUCCESS,
  CLEAR_CHAT_CONTEXT,
  GET_CONVERSATIONS_REQUEST,
  GET_CONVERSATIONS_SUCCESS,
  GET_CONVERSATION_FAILURE,
  GET_MESSAGES_FAILURE,
  GET_MESSAGES_REQUEST,
  GET_MESSAGES_SUCCESS,
  GET_UNREAD_MESSAGES_COUNT_FAILURE,
  GET_UNREAD_MESSAGES_COUNT_REQUEST,
  GET_UNREAD_MESSAGES_COUNT_SUCCESS,
  SEND_MESSAGE_FAILURE,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SET_ALL_MESSAGES_READ_FAILURE,
  SET_ALL_MESSAGES_READ_REQUEST,
  SET_ALL_MESSAGES_READ_SUCCESS,
} from 'state/actionTypes/chatActionTypes';
import { useTwilioContext } from '../TwilioContext/TwilioContext';
import { ChatAction, ChatReducer, ChatState } from './ChatContext.types';

type ChatContext = {
  chatState: ChatState;
  dispatch: Dispatch<ChatAction>;
  setMessageLoader: Dispatch<SetStateAction<boolean>>;
  messageLoader: boolean;
  hasUnreadMessages: boolean;
  setSelectedStorageRequestSid: Dispatch<SetStateAction<string>>;
  selectedStorageRequestSid: string;
};
type UseChatContext = () => ChatContext;

const ChatContext = createContext({} as ChatContext);

const INITIAL_STATE: ChatState = {
  getConversationRequest: { status: 'Initial' },
  getMessagesRequest: { status: 'Initial' },
  sendMessageRequest: { status: 'Initial' },
  getPreviousMessagesRequest: { status: 'Initial' },
  setMessagesCountRequest: { status: 'Initial' },
  conversations: null,
  conversationMessages: null,
  messagesType: 'current',
};

const chatReducer: ChatReducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case GET_CONVERSATIONS_REQUEST:
      return update(state, {
        getConversationRequest: { status: 'InProgress' },
      });

    case GET_CONVERSATIONS_SUCCESS:
      return update(state, {
        getConversationRequest: { status: 'Success' },
        conversations: action.payload,
      });

    case GET_CONVERSATION_FAILURE:
      return update(state, {
        getConversationRequest: { status: 'Error', error: action.payload },
        conversations: null,
      });

    case GET_MESSAGES_REQUEST:
      return update(state, {
        getMessagesRequest: { status: 'InProgress' },
      });

    case GET_MESSAGES_SUCCESS:
      return update(state, {
        getMessagesRequest: { status: 'Success' },
        conversationMessages: action.payload.conversationMessages,
      });

    case GET_MESSAGES_FAILURE:
      return update(state, {
        getMessagesRequest: { status: 'Error', error: action.payload },
        conversationMessages: null,
      });

    case SEND_MESSAGE_REQUEST:
      return update(state, {
        sendMessageRequest: { status: 'InProgress' },
      });

    case SEND_MESSAGE_SUCCESS:
      return update(state, {
        sendMessageRequest: { status: 'Success' },
      });

    case SEND_MESSAGE_FAILURE:
      return update(state, {
        sendMessageRequest: { status: 'Error', error: action.payload },
      });

    case ADD_MESSAGE:
      return update(state, {
        conversationMessages: state.conversationMessages
          ? {
              ...state.conversationMessages,
              [action.payload.conversationSid]: {
                ...state.conversationMessages[action.payload.conversationSid],
                messages: [
                  ...state.conversationMessages[action.payload.conversationSid].messages,
                  action.payload.message,
                ],
              },
            }
          : null,
        messagesType: 'current',
      });

    case APPEND_PREVIOUS_MESSAGE_REQUEST:
      return update(state, {
        getPreviousMessagesRequest: { status: 'InProgress' },
      });

    case APPEND_PREVIOUS_MESSAGE_SUCCESS:
      return update(state, {
        conversationMessages: state.conversationMessages
          ? {
              ...state.conversationMessages,
              [action.payload.sid]: {
                ...state.conversationMessages[action.payload.sid],
                paginator: action.payload.paginator,
                messages: [
                  ...action.payload.paginator.items,
                  ...state.conversationMessages[action.payload.sid].messages,
                ],
              },
            }
          : null,
        getPreviousMessagesRequest: { status: 'Success' },
        messagesType: 'previous',
      });

    case APPEND_PREVIOUS_MESSAGE_FAILURE:
      return update(state, {
        getPreviousMessagesRequest: { status: 'Error', error: action.payload },
      });

    case GET_UNREAD_MESSAGES_COUNT_REQUEST:
      return update(state, {
        setMessagesCountRequest: { status: 'InProgress' },
      });

    case GET_UNREAD_MESSAGES_COUNT_SUCCESS:
      return update(state, {
        setMessagesCountRequest: { status: 'Success' },
        conversationMessages: state.conversationMessages
          ? {
              ...state.conversationMessages,
              [action.payload.sid]: {
                ...state.conversationMessages[action.payload.sid],
                unreadMessagesCount: action.payload.unreadMessagesCount,
              },
            }
          : null,
      });

    case GET_UNREAD_MESSAGES_COUNT_FAILURE:
      return update(state, {
        setMessagesCountRequest: { status: 'Error', error: action.payload },
      });

    case SET_ALL_MESSAGES_READ_REQUEST:
      return update(state, {
        setMessagesCountRequest: { status: 'InProgress' },
      });

    case SET_ALL_MESSAGES_READ_SUCCESS:
      return update(state, {
        setMessagesCountRequest: { status: 'Success' },
        conversationMessages: state.conversationMessages
          ? {
              ...state.conversationMessages,
              [action.payload]: {
                ...state.conversationMessages[action.payload],
                unreadMessagesCount: 0,
              },
            }
          : null,
      });

    case SET_ALL_MESSAGES_READ_FAILURE:
      return update(state, {
        setMessagesCountRequest: { status: 'Error', error: action.payload },
      });

    case CLEAR_CHAT_CONTEXT:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export const ChatProvider: FC = ({ children }) => {
  const [chatState, dispatch] = useReducer(chatReducer, INITIAL_STATE);
  const { conversationMessages } = chatState;
  const { client } = useTwilioContext();
  const [selectedStorageRequestSid, setSelectedStorageRequestSid] = useState('');

  useEffect(() => {
    if (client) {
      const getConversationsMessages = async (client: Client) => {
        const c = await getConversations(dispatch, client);
        await getMessages(dispatch, c as Conversation[]);
      };
      getConversationsMessages(client);
    }

    return () => {
      client?.removeAllListeners();
    };
  }, [client]);

  useEffect(() => {
    if (client) {
      client.on('messageAdded', async (message) => {
        if (selectedStorageRequestSid === message.conversation.sid) {
          message.conversation.updateLastReadMessageIndex(message.index);
          setAllMessagesReadAction(dispatch, message.conversation);
        } else {
          getUnreadMessagesCountAction(dispatch, message.conversation);
        }
        addMessage(dispatch, {
          message,
          conversationSid: message.conversation.sid,
        });
      });

      return () => {
        client.removeAllListeners();
      };
    }
  }, [client, selectedStorageRequestSid]);

  useEffect(() => () => dispatch({ type: CLEAR_CHAT_CONTEXT }), [dispatch]);

  const loadingMessages = chatState.getMessagesRequest.status === 'InProgress';
  const initMessages = chatState.getMessagesRequest.status === 'Initial';

  const [messageLoader, setMessageLoader] = useState(true);

  const hasUnreadMessages = !!(
    conversationMessages &&
    Object.values(conversationMessages).some(
      (conversation) => conversation.unreadMessagesCount > 0,
    )
  );

  useEffect(() => {
    setTimeout(() => {
      if (messageLoader && !(initMessages || loadingMessages)) {
        setMessageLoader(false);
      }
    }, 300);
  }, [initMessages, loadingMessages, messageLoader]);

  return (
    <ChatContext.Provider
      value={{
        chatState,
        dispatch,
        setMessageLoader,
        messageLoader,
        hasUnreadMessages,
        setSelectedStorageRequestSid,
        selectedStorageRequestSid,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext: UseChatContext = () => useContext(ChatContext);
